// src/oura/ask/images.ts
//
// Pictures sent with a question in Ask Oura (behind VITE_ENABLE_OURA_ASK_IMAGES):
// a screenshot of a Google result, a client's WhatsApp, a page that looks
// wrong. Up to 3 per question, PNG, JPEG or WebP, each shrunk to 1600px on its
// long side before it goes, so a phone photo does not send 8 MB.

import type { AskImage } from "./types";

export const ASK_IMAGES_MAX = 3;
const MAX_BYTES = 12 * 1024 * 1024;
const LONG_SIDE = 1600;
const OK_TYPE = /^image\/(png|jpe?g|webp)$/i;
export const ASK_IMAGES_ACCEPT = ".png,.jpg,.jpeg,.webp";

/** The files that may go, and the sentence for the first one refused. */
export function takeImageFiles(current: number, incoming: File[]): { files: File[]; error: string | null } {
    const files: File[] = [];
    let error: string | null = null;
    for (const f of incoming) {
        if (!OK_TYPE.test(f.type) && !/\.(png|jpe?g|webp)$/i.test(f.name)) {
            error ??= `${f.name || "That file"} is not a picture. Use PNG, JPEG or WebP.`;
            continue;
        }
        if (f.size > MAX_BYTES) {
            error ??= `${f.name || "That file"} is over 12 MB.`;
            continue;
        }
        if (current + files.length >= ASK_IMAGES_MAX) {
            error ??= `Up to ${ASK_IMAGES_MAX} pictures with one question.`;
            break;
        }
        files.push(f);
    }
    return { files, error };
}

const readDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(blob);
    });

/**
 * The picture as the chat keeps it. Every picture is decoded and re-encoded, so the
 * bytes always match the mime the server is told (a renamed non-image rejects here),
 * and anything over 1600px on its long side is shrunk. PNG stays PNG so a
 * screenshot's text stays crisp; anything else goes as JPEG.
 */
export async function toAskImage(file: File, index: number): Promise<AskImage> {
    const bitmap = await createImageBitmap(file);
    try {
        const long = Math.max(bitmap.width, bitmap.height);
        if (!long || bitmap.width < 1 || bitmap.height < 1) throw new Error("Image has no size");
        const scale = Math.min(1, LONG_SIDE / long);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas unavailable");
        const isPng = file.type.toLowerCase() === "image/png" || (!file.type && /\.png$/i.test(file.name));
        const mime = isPng ? "image/png" : "image/jpeg";
        // JPEG has no transparency: a see-through WebP would otherwise come out black on black.
        if (!isPng) {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((out) => (out ? resolve(out) : reject(new Error("Image encoding failed"))), mime, 0.9);
        });
        const url = await readDataUrl(blob);
        // A pasted screenshot arrives as "image.png": name it so the answer can refer to it.
        const stem = file.name.replace(/\.(png|jpe?g|webp)$/i, "").trim();
        const name = `${!stem || /^image$/i.test(stem) ? `Picture ${index + 1}` : stem}.${isPng ? "png" : "jpg"}`;
        return { name, mime, data: url.slice(url.indexOf(",") + 1), url };
    } finally {
        bitmap.close();
    }
}
